import React, { useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, TooltipItem } from 'chart.js';
import { supabase } from '../supabaseClient';
import { useAuth } from '../context/AuthContext';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

// Set default font for all charts
ChartJS.defaults.font.family = "'Quicksand', sans-serif";
ChartJS.defaults.font.weight = 300;

const Analytics: React.FC = () => {
  const { currentUser } = useAuth();
  const [moodData, setMoodData] = useState<any>({ labels: [], datasets: [] });
  const [sentimentData, setSentimentData] = useState<any>({ labels: [], datasets: [] });
  const [insights, setInsights] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [insightsLoading, setInsightsLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('week'); // 'week' or 'month'

  useEffect(() => {
    const fetchData = async () => {
      if (!currentUser) return;
      setLoading(true);

      const days = timeRange === 'week' ? 7 : 30;
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days + 1);
      startDate.setHours(0, 0, 0, 0);

      const { data: journals, error } = await supabase
        .from('journals')
        .select('created_at, emotions, sentiment')
        .eq('user_id', currentUser.id)
        .gte('created_at', startDate.toISOString());

      if (error) {
        console.error('Error fetching journal data:', error);
        setLoading(false);
        return;
      }

      if (journals) {
        const dailyData: { [date: string]: { emotionTotal: number; sentimentTotal: number; count: number } } = {};
        const dateLabels: string[] = [];

        for (let i = 0; i < days; i++) {
          const date = new Date(startDate);
          date.setDate(startDate.getDate() + i);
          const dateString = date.toLocaleDateString();
          dateLabels.push(dateString);
          dailyData[dateString] = { emotionTotal: 0, sentimentTotal: 0, count: 0 };
        }

        journals.forEach(journal => {
          const date = new Date(journal.created_at).toLocaleDateString();
          if (dailyData[date]) {
            dailyData[date].emotionTotal += journal.emotions || 0;
            dailyData[date].sentimentTotal += journal.sentiment || 0;
            dailyData[date].count++;
          }
        });

        const moodDataPoints = dateLabels.map(date => {
          const dayData = dailyData[date];
          return dayData.count > 0 ? dayData.emotionTotal / dayData.count : 0;
        });

        const sentimentDataPoints = dateLabels.map(date => {
          const dayData = dailyData[date];
          return dayData.count > 0 ? dayData.sentimentTotal / dayData.count : 0;
        });

        const moodPointColors = moodDataPoints.map(value => value === 0 ? 'black' : '#6b8e6b');
        const sentimentPointColors = sentimentDataPoints.map(value => value === 0 ? 'black' : '#81c784');

        setMoodData({
          labels: dateLabels,
          datasets: [{
            label: 'Average Mood Score',
            data: moodDataPoints,
            borderColor: '#6b8e6b',
            backgroundColor: 'rgba(107, 142, 107, 0.2)',
            pointBackgroundColor: moodPointColors,
            pointBorderColor: moodPointColors,
            fill: false,
          }],
        });

        setSentimentData({
          labels: dateLabels,
          datasets: [{
            label: 'Average Sentiment Score',
            data: sentimentDataPoints,
            borderColor: '#81c784',
            backgroundColor: 'rgba(129, 199, 132, 0.2)',
            pointBackgroundColor: sentimentPointColors,
            pointBorderColor: sentimentPointColors,
            fill: false,
          }],
        });

        const fetchInsights = async () => {
          setInsightsLoading(true);
          const moodApiData = dateLabels.map((date, i) => [date, moodDataPoints[i]]);
          const sentimentApiData = dateLabels.map((date, i) => [date, sentimentDataPoints[i]]);

          try {
            const response = await fetch('/api/insights', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ moodData: moodApiData, sentimentData: sentimentApiData }),
            });

            if (!response.ok) {
              throw new Error('Failed to fetch insights');
            }

            const result = await response.json();
            setInsights(result.insights || '');
          } catch (error) {
            console.error('Error fetching insights:', error);
            setInsights(''); // Clear insights on error
          } finally {
            setInsightsLoading(false);
          }
        };

        fetchInsights();
      }
      setLoading(false);
    };

    fetchData();
  }, [currentUser, timeRange]);

  const moodOptions = {
    responsive: true,
    maintainAspectRatio: false,
    clip: false,
    plugins: {
      legend: { position: 'top' as const },
      title: { display: true, text: 'Mood Fluctuation Over Time' },
      tooltip: {
        displayColors: false,
        callbacks: {
          label: function(context: TooltipItem<'line'>) {
            if (context.raw === 0) {
              return [
                "You didn't journal this day,",
                "so we sadly don't have any information.",
                "We recommend writing something each day",
                "to best track your progress!"
              ];
            }
            return `Mood Score: ${context.formattedValue}`;
          }
        }
      }
    },
    scales: {
      y: {
        min: 0,
        max: 5,
      },
    },
  } as const;

  const sentimentOptions = {
    responsive: true,
    maintainAspectRatio: false,
    clip: false,
    plugins: {
      legend: { position: 'top' as const },
      title: { display: true, text: 'Sentiment Fluctuation Over Time' },
      tooltip: {
        displayColors: false,
        callbacks: {
          label: function(context: TooltipItem<'line'>) {
            if (context.raw === 0) {
              return [
                "You didn't journal this day,",
                "so we sadly don't have any information.",
                "We recommend writing something each day",
                "to best track your progress!"
              ];
            }
            return `Sentiment Score: ${context.formattedValue}`;
          }
        }
      }
    },
    scales: {
      y: {
        min: -2,
        max: 2,
        ticks: {
          stepSize: 1,
        },
      },
    },
  } as const;

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <main className="main-content analytics-page">
      <div className="analytics-container">
        <h2>Analytics</h2>
        <div className="time-range-filter">
          <button onClick={() => setTimeRange('week')} className={timeRange === 'week' ? 'active' : ''}>Past Week</button>
          <button onClick={() => setTimeRange('month')} className={timeRange === 'month' ? 'active' : ''}>Past Month</button>
        </div>

        <div className="insights-container">
            <h3>Insights</h3>
            {insightsLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100px' }}>
                    <div className="loader"></div>
                </div>
            ) : (
                <p>{insights}</p>
            )}
        </div>
        <div className="chart-container">
          <h3>Daily Average Mood</h3>
          <Line options={moodOptions} data={moodData} />
        </div>
        <div className="chart-container">
          <h3>Daily Average Sentiment</h3>
          <Line options={sentimentOptions} data={sentimentData} />
        </div>
      </div>
    </main>
  );
};

export default Analytics;
